import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { CompanyAssetLibrary } from '../../components/admin/CompanyAssetLibrary';
import { CompanyTemplatesManager } from '../../components/admin/CompanyTemplatesManager';
import { useViewport } from '../../hooks/useViewport';
import { 
  useCompany, 
  useUpdateCompany, 
  useCreateCompany,
  useCompanyAssets
} from '../../hooks/unified/useCompaniesQueries';
import { useToast } from '../../contexts/ToastContext';
import { env } from '../../config/env';
import type { Company, CreateCompanyRequest, UpdateCompanyRequest, CompanyAsset } from '../../services/unified/types';

export function AdminCompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useViewport(); // For responsive behavior
  const { showSuccess, showError } = useToast();
  const isNew = id === 'new';

  const { data: company, isLoading } = useCompany(isNew ? '' : (id || ''));
  const { data: assets = [] } = useCompanyAssets(isNew ? '' : (id || ''));
  const updateMutation = useUpdateCompany();
  const createMutation = useCreateCompany();

  // Helper function to get full asset URL
  const getAssetUrl = (asset: CompanyAsset): string => {
    if (asset.url.startsWith('http://') || asset.url.startsWith('https://')) {
      return asset.url;
    }
    return `${env.API_BASE_URL}${asset.url}`;
  };

  const [formData, setFormData] = useState<Partial<Company>>({
    name: '',
    description: '',
    website: '',
    industry: '',
    company_size: '',
    founded_year: undefined,
    headquarters: undefined,
    operating_locations: undefined,
    culture: undefined,
    benefits: undefined,
  });

  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  
  // Temporary state for comma-separated fields
  const [coreValuesText, setCoreValuesText] = useState('');
  const [perksText, setPerksText] = useState('');

  useEffect(() => {
    if (company && !isNew) {
      const parsedCulture = typeof company.culture === 'string' 
        ? JSON.parse(company.culture || '{}')
        : company.culture;
      
      setFormData({
        name: company.name,
        description: company.description,
        website: company.website,
        industry: company.industry,
        company_size: company.company_size,
        founded_year: company.founded_year,
        headquarters: typeof company.headquarters === 'string' 
          ? JSON.parse(company.headquarters || '{}')
          : company.headquarters,
        operating_locations: typeof company.operating_locations === 'string'
          ? JSON.parse(company.operating_locations || '[]')
          : company.operating_locations,
        culture: parsedCulture,
        benefits: typeof company.benefits === 'string'
          ? JSON.parse(company.benefits || '[]')
          : company.benefits,
      });
      
      // Set text fields for comma-separated values
      setCoreValuesText((parsedCulture?.values || []).join(', '));
      setPerksText((parsedCulture?.perks || []).join(', '));
    }
  }, [company, isNew]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      showError('Company name is required');
      return;
    }

    // Process comma-separated fields before submitting
    const processedData = {
      ...formData,
      culture: {
        ...(formData.culture as any || {}),
        values: coreValuesText.split(',').map(v => v.trim()).filter(v => v),
        perks: perksText.split(',').map(v => v.trim()).filter(v => v),
      }
    };

    try {
      if (isNew) {
        const newCompany = await createMutation.mutateAsync(processedData as CreateCompanyRequest);
        showSuccess('Company created successfully');
        navigate(`/admin/companies/${newCompany.id}`);
      } else if (id) {
        await updateMutation.mutateAsync({ id, data: processedData as UpdateCompanyRequest });
        showSuccess('Company updated successfully');
      }
    } catch (error) {
      showError(isNew ? 'Failed to create company' : 'Failed to update company');
    }
  };

  const handleCancel = () => {
    navigate('/admin/companies');
  };

  if (isLoading && !isNew) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={handleCancel}
            className="text-gray-600 hover:text-gray-900 mb-4 flex items-center min-h-[44px]"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Companies
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {isNew ? 'Create New Company' : `Edit ${company?.name}`}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Brief description of the company..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website || ''}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry
                </label>
                <input
                  type="text"
                  value={formData.industry || ''}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  placeholder="e.g., Technology, Finance, Healthcare"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Size
                </label>
                <select
                  value={formData.company_size || ''}
                  onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                >
                  <option value="">Select size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="501-1000">501-1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Founded Year
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={formData.founded_year || ''}
                  onChange={(e) => setFormData({ ...formData, founded_year: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  placeholder="e.g., 2010"
                  min="1800"
                  max={new Date().getFullYear()}
                />
              </div>
            </div>
          </div>

          {/* Headquarters Section */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Headquarters</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={(formData.headquarters as any)?.city || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    headquarters: { ...(formData.headquarters as any || {}), city: e.target.value }
                  })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  placeholder="e.g., San Francisco"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State/Province
                </label>
                <input
                  type="text"
                  value={(formData.headquarters as any)?.state || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    headquarters: { ...(formData.headquarters as any || {}), state: e.target.value }
                  })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  placeholder="e.g., CA"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={(formData.headquarters as any)?.country || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    headquarters: { ...(formData.headquarters as any || {}), country: e.target.value }
                  })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  placeholder="e.g., USA"
                />
              </div>
            </div>
          </div>

          {/* Operating Locations Section */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Operating Locations</h2>
            <div className="space-y-3">
              {((formData.operating_locations as any[]) || []).map((location: any, index: number) => (
                <div key={index} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 bg-gray-50 rounded-md">
                  <input
                    type="text"
                    value={location.city || ''}
                    onChange={(e) => {
                      const newLocations = [...((formData.operating_locations as any[]) || [])];
                      newLocations[index] = { ...newLocations[index], city: e.target.value };
                      setFormData({ ...formData, operating_locations: newLocations });
                    }}
                    className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                    placeholder="City"
                  />
                  <input
                    type="text"
                    value={location.state || ''}
                    onChange={(e) => {
                      const newLocations = [...((formData.operating_locations as any[]) || [])];
                      newLocations[index] = { ...newLocations[index], state: e.target.value };
                      setFormData({ ...formData, operating_locations: newLocations });
                    }}
                    className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                    placeholder="State"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={location.country || ''}
                      onChange={(e) => {
                        const newLocations = [...((formData.operating_locations as any[]) || [])];
                        newLocations[index] = { ...newLocations[index], country: e.target.value };
                        setFormData({ ...formData, operating_locations: newLocations });
                      }}
                      className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                      placeholder="Country"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newLocations = ((formData.operating_locations as any[]) || []).filter((_, i) => i !== index);
                        setFormData({ ...formData, operating_locations: newLocations });
                      }}
                      className="p-2.5 text-red-600 hover:bg-red-50 rounded-md min-w-[44px] min-h-[44px] flex items-center justify-center"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const newLocations = [...((formData.operating_locations as any[]) || []), { city: '', state: '', country: '' }];
                  setFormData({ ...formData, operating_locations: newLocations });
                }}
                className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-primary-500 hover:text-primary-600 min-h-[44px]"
              >
                + Add Location
              </button>
            </div>
          </div>

          {/* Company Culture Section */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Company Culture</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Core Values (comma-separated)
                </label>
                <input
                  type="text"
                  value={coreValuesText}
                  onChange={(e) => setCoreValuesText(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  placeholder="e.g., Innovation, Collaboration, Integrity"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Work Environment
                </label>
                <select
                  value={(formData.culture as any)?.work_environment || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    culture: { ...(formData.culture as any || {}), work_environment: e.target.value }
                  })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                >
                  <option value="">Select environment</option>
                  <option value="Remote">Remote</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="On-site">On-site</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Perks (comma-separated)
                </label>
                <input
                  type="text"
                  value={perksText}
                  onChange={(e) => setPerksText(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  placeholder="e.g., Free lunch, Gym membership, Learning budget"
                />
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Benefits</h2>
            <div className="space-y-3">
              {((formData.benefits as string[]) || []).map((benefit: string, index: number) => (
                <div key={index} className="flex items-center gap-2 sm:gap-3">
                  <input
                    type="text"
                    value={benefit}
                    onChange={(e) => {
                      const newBenefits = [...((formData.benefits as string[]) || [])];
                      newBenefits[index] = e.target.value;
                      setFormData({ ...formData, benefits: newBenefits });
                    }}
                    className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                    placeholder="e.g., Health Insurance"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newBenefits = ((formData.benefits as string[]) || []).filter((_, i) => i !== index);
                      setFormData({ ...formData, benefits: newBenefits });
                    }}
                    className="p-2.5 text-red-600 hover:bg-red-50 rounded-md min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const newBenefits = [...((formData.benefits as string[]) || []), ''];
                  setFormData({ ...formData, benefits: newBenefits });
                }}
                className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-primary-500 hover:text-primary-600 min-h-[44px]"
              >
                + Add Benefit
              </button>
            </div>
          </div>

          {/* Assets Section */}
          {!isNew && (
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Company Assets</h2>
                <button
                  type="button"
                  onClick={() => setShowAssetLibrary(true)}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium min-h-[44px] flex items-center"
                >
                  Manage Assets
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {assets.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No assets uploaded yet
                  </div>
                ) : (
                  assets.map((asset) => (
                    <div key={asset.id} className="relative group">
                      <img
                        src={getAssetUrl(asset)}
                        alt={asset.filename}
                        className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      {asset.is_default && (
                        <span className="absolute top-2 right-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                          Default
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 sm:space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="w-full sm:w-auto px-6 py-2.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 min-h-[44px]"
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : isNew
                ? 'Create Company'
                : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* Job Templates Section */}
        {!isNew && id && (
          <div className="mt-6">
            <CompanyTemplatesManager companyId={id} />
          </div>
        )}

        {/* Asset Library Modal */}
        {!isNew && (
          <CompanyAssetLibrary
            isOpen={showAssetLibrary}
            onClose={() => setShowAssetLibrary(false)}
            onAssetSelected={() => {}}
            assetType="both"
          />
        )}
      </div>
    </AdminLayout>
  );
}
